// ============================================================================
// CEO ANALYTICS DATA TRANSFORMS
// ============================================================================
//
// Pure functions that convert raw deal/metric data into chart-ready formats.
// No hooks, no side effects — safe to call inside useMemo.
//
// ============================================================================

import { Deal } from "@/lib/types/database/deals";
import { isWonDeal } from "@/lib/constants/deal-stages";
import { parseBigFloat } from "@wtc/ui";
import {
  weightedPipelineContribution,
  committedRevenueContribution,
} from "@/lib/dashboard/metrics/forecast";
import { classifyCompanyHealth } from "@/components/admin/ceo-dashboard/health-dimensions";
import type {
  HealthCompanyNode,
  HealthUserNode,
  HealthTimeEntryNode,
} from "./health-metrics-calculator";
import {
  WinRateTrendData,
  ACVDistributionData,
  ConversionFunnelData,
  CompanyScatterPoint,
  UtilizationData,
  ForecastTrendPoint,
} from "./ceo-analytics-types";

/** Health score (0-100) the scatter plots on its Y axis, by classified bucket. */
const HEALTH_SCORE_BY_BUCKET = { red: 30, yellow: 60, green: 85 } as const;

/** Whole days from `from` to `to` (`to - from`); negative when `to` is in the past. */
function dayDelta(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
}

/** YYYY-MM-DD from an ISO timestamp; skips rows with missing timestamps. */
function toCreatedDateKey(createdAt: string | null | undefined): string | null {
  if (!createdAt) return null;
  const day = createdAt.split("T")[0];
  return day.length > 0 ? day : null;
}

/**
 * Build a 30-day win rate trend from a list of deals.
 * Each point reflects deals created on that day and how many were won.
 */
export function transformWinRateTrendData(deals: Deal[]): WinRateTrendData[] {
  const trendData: WinRateTrendData[] = [];

  for (let dayOffset = 29; dayOffset >= 0; dayOffset--) {
    const date = new Date();
    date.setDate(date.getDate() - dayOffset);
    const dateStr = date.toISOString().split("T")[0];

    const dealsCreatedThatDay = deals.filter(
      (d) => toCreatedDateKey(d.created_at) === dateStr
    );
    const wonDeals = dealsCreatedThatDay.filter((d) => isWonDeal(d.stage)).length;
    const totalDeals = dealsCreatedThatDay.length;
    const winRate = totalDeals > 0 ? (wonDeals / totalDeals) * 100 : 0;

    trendData.push({
      date: dateStr,
      winRate: Math.round(winRate * 100) / 100,
      wonDeals,
      totalDeals,
      dayIndex: 29 - dayOffset,
    });
  }

  return trendData;
}

/**
 * Distribute won deals into revenue buckets for ACV distribution chart.
 */
export function transformACVDistributionData(
  deals: Deal[],
  bucketPreset: any
): ACVDistributionData[] {
  const buckets: ACVDistributionData[] = bucketPreset.buckets.map((bucket: any) => ({
    bucket: bucket.label,
    count: 0,
    totalValue: 0,
    minAmount: bucket.minAmount,
    maxAmount: bucket.maxAmount,
  }));

  const wonDeals = deals.filter((d) => isWonDeal(d.stage));

  wonDeals.forEach((deal) => {
    const amount = deal.revenue || 0;
    const bucket = buckets.find(
      (b) => amount >= b.minAmount && amount < b.maxAmount
    );
    if (bucket) {
      bucket.count++;
      bucket.totalValue += amount;
    }
  });

  return buckets;
}

/**
 * Build a three-stage conversion funnel: communications → deals created → deals won.
 */
export function transformConversionFunnelData(
  deals: Deal[],
  communicationCount: number
): ConversionFunnelData[] {
  const totalDealsCreated = deals.length;
  const totalDealsWon = deals.filter((d) => isWonDeal(d.stage)).length;

  return [
    {
      stage: "Communications",
      count: communicationCount,
      percentage: 100,
    },
    {
      stage: "Deals Created",
      count: totalDealsCreated,
      percentage:
        communicationCount > 0
          ? (totalDealsCreated / communicationCount) * 100
          : 0,
    },
    {
      stage: "Deals Won",
      count: totalDealsWon,
      percentage:
        totalDealsCreated > 0 ? (totalDealsWon / totalDealsCreated) * 100 : 0,
    },
  ];
}

/**
 * Map rated companies to scatter points using their REAL data:
 * organization_revenue (X), classified 4-dimension health (Y + status), and
 * the company's deal count (bubble size). Unrated companies are skipped.
 * Caps at 20 points (the chart is unreadable beyond that).
 */
export function transformCompanyScatterData(
  companies: HealthCompanyNode[]
): CompanyScatterPoint[] {
  const points: CompanyScatterPoint[] = [];
  companies.slice(0, 20).forEach((company) => {
    const bucket = classifyCompanyHealth(company);
    if (!bucket) return; // unrated — nothing to plot
    points.push({
      id: company.id,
      name: company.name ?? "Unknown",
      revenue: parseBigFloat(company.organization_revenue) ?? 0,
      health: HEALTH_SCORE_BY_BUCKET[bucket],
      deals: company.dealsCollection?.totalCount ?? 0,
      status: bucket,
    });
  });
  return points;
}

/**
 * Per-billable-user utilization: hours logged (from the period's time entries,
 * grouped by user_id) over available capacity (available_hours × weeksInPeriod).
 * Non-billable users have no capacity target and are excluded. Percentage is NOT
 * clamped — the heatmap surfaces overallocation (>100%).
 */
export function transformTeamUtilizationData(
  users: HealthUserNode[],
  timeEntries: HealthTimeEntryNode[],
  weeksInPeriod: number
): UtilizationData[] {
  const hoursByUser = new Map<string, number>();
  timeEntries.forEach((entry) => {
    if (!entry.user_id) return;
    const hours = parseBigFloat(entry.hours) ?? 0;
    hoursByUser.set(entry.user_id, (hoursByUser.get(entry.user_id) ?? 0) + hours);
  });

  const result: UtilizationData[] = [];
  users.forEach((user) => {
    if (!user.is_billable) return;
    const availableHours = (user.available_hours ?? 40) * weeksInPeriod;
    const worked = hoursByUser.get(user.id) ?? 0;
    const percentage =
      availableHours > 0 ? Math.round((worked / availableHours) * 100) : 0;
    const name =
      [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
      user.email ||
      "Unknown";
    result.push({
      userId: user.id,
      userName: name,
      percentage,
      status:
        percentage > 100 ? "overallocated" :
        percentage >= 80 ? "warning" : "healthy",
    });
  });
  return result;
}

/**
 * Cumulative revenue-forecast trend bucketed by close_date. For each 10-day
 * checkpoint to 90 days, sums the canonical weighted/committed contribution of
 * every deal expected to close on or before that day (deals already past their
 * close_date count at day 0). Monotonic — a real projection curve, not a
 * single value repeated across the axis.
 */
export function transformForecastTrendData(
  deals: Deal[],
  targetAmount: number,
  today: Date
): ForecastTrendPoint[] {
  if (deals.length === 0) return [];

  const dealsWithCloseDay = deals.map((deal) => ({
    deal,
    closeDay: deal.close_date ? dayDelta(today, new Date(deal.close_date)) : 0,
  }));

  const points: ForecastTrendPoint[] = [];
  for (let day = 0; day <= 90; day += 10) {
    const inWindow = dealsWithCloseDay.filter((d) => d.closeDay <= day);
    const weighted = inWindow.reduce(
      (sum, { deal }) => sum + weightedPipelineContribution(deal),
      0
    );
    const committed = inWindow.reduce(
      (sum, { deal }) => sum + committedRevenueContribution(deal),
      0
    );
    const date = new Date(today);
    date.setUTCDate(date.getUTCDate() + day);
    points.push({
      day,
      weighted: Math.round(weighted),
      committed: Math.round(committed),
      target: Math.round(targetAmount),
      date: date.toISOString().split("T")[0],
    });
  }
  return points;
}
